import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../lib/i18n";
import {
  MapPin,
  Building2,
  Zap,
  Compass,
  Users,
  Sparkles,
  Instagram,
  ArrowRight,
  Loader2,
  Check,
  Globe,
  Info,
  Clock,
  Accessibility,
  Radio,
  MapPinned,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import AppShowcase from "../components/AppShowcase";
import Roadmap from "../components/Roadmap";
import InviteSection from "../components/InviteSection";
import SupportSection from "../components/SupportSection";
import CampaignTeaser from "../components/CampaignTeaser";
import UseCases from "../components/UseCases";
import { InstallButton } from "../components/InstallCTA";
import { PLAY_URL, PLAY_BADGE_SRC } from "../lib/appLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_IMG = "/hero-phone.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Nav = () => {
  const { t } = useLang();
  return (
  <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0a0a0c]/70 border-b border-white/5">
    <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-12 h-14 md:h-16">
      <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
        <div className="relative w-7 h-7 md:w-8 md:h-8 rounded-md bg-[#ccff00] flex items-center justify-center">
          <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#0a0a0c]" strokeWidth={2.5} />
        </div>
        <span className="font-heading font-semibold tracking-tight text-sm md:text-base">
          SpotDrop<span className="text-[#ccff00]">.</span>
        </span>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
        <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">{t("nav.howItWorks")}</a>
        <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">{t("nav.features")}</a>
        <a href="#invite" className="hover:text-white transition-colors" data-testid="nav-invite">Invite friends</a>
        <a href="#waitlist" className="hover:text-white transition-colors" data-testid="nav-waitlist">{t("nav.updates")}</a>
      </nav>
      <div className="flex items-center gap-2 md:gap-3">
        <LanguageSelector />
        <a
          href="/app"
          className="inline-flex items-center gap-1.5 px-3 md:px-4 h-8 md:h-9 rounded-full bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] text-xs md:text-sm font-semibold transition-colors"
          data-testid="nav-open-app"
        >
          {t("nav.useSpotDrop")}
        </a>
      </div>
    </div>
  </header>
  );
};

const LanguageSelector = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const langs = [
    { code: "en", label: t("nav.langLabel.en") },
    { code: "mt", label: t("nav.langLabel.mt") },
    { code: "it", label: t("nav.langLabel.it") },
  ];
  const activeCode = lang.toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 md:px-3 h-8 md:h-9 rounded-full border border-white/10 hover:border-white/20 text-xs md:text-sm text-zinc-300 transition-colors active:scale-95"
        data-testid="lang-selector"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.language")}
      >
        <Globe className="w-3.5 h-3.5 text-zinc-400" />
        <span className="font-medium">{activeCode}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-44 max-w-[calc(100vw-1.5rem)] rounded-xl bg-[#141416] border border-white/10 shadow-xl overflow-hidden z-50"
          data-testid="lang-menu"
          role="listbox"
        >
          {langs.map((l) => {
            const isActive = l.code === lang;
            return (
              <button
                type="button"
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
                  isActive ? "text-white bg-white/5" : "text-zinc-300 hover:bg-white/5"
                }`}
                data-testid={`lang-${l.code.toUpperCase()}`}
                role="option"
                aria-selected={isActive}
              >
                <span>
                  <span className="font-medium mr-1.5">{l.code.toUpperCase()}</span>
                  <span className="text-zinc-500">{l.label}</span>
                </span>
                {isActive && (
                  <Check className="w-3.5 h-3.5 text-[#ccff00]" strokeWidth={2.5} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MapPinFloating = ({ className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8 }}
    className={`absolute ${className}`}
  >
    <div className="relative">
      <div className="relative w-3 h-3 rounded-full bg-[#ccff00] pin-pulse shadow-[0_0_18px_rgba(204,255,0,0.9)]" />
    </div>
  </motion.div>
);

const Hero = () => {
  const { t } = useLang();
  return (
  <section id="top" className="relative pt-24 md:pt-40 pb-16 md:pb-24 overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
    <div className="absolute inset-0 radial-fade pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
      <div className="lg:col-span-7 text-center lg:text-left">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[10px] md:text-xs uppercase tracking-[0.18em] text-zinc-400"
          data-testid="hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#ccff00] animate-pulse" />
          {t("hero.badge")}
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={1}
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mt-5 md:mt-6 leading-[1.05]"
          data-testid="hero-headline"
        >
          {t("hero.headline")}
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={2}
          className="mt-5 md:mt-6 text-base md:text-xl text-zinc-400 max-w-xl mx-auto lg:mx-0 leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={3}
          className="mt-8 md:mt-10 flex flex-col items-center lg:items-start gap-4"
        >
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex shrink-0 transition-transform hover:scale-[1.03] active:scale-95"
              data-testid="hero-google-play"
              aria-label={t("hero.ctaPlayAlt")}
            >
              <img
                src={PLAY_BADGE_SRC}
                alt={t("hero.ctaPlayAlt")}
                width={646}
                height={250}
                className="h-[54px] w-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
              />
            </a>
            <a href="/app" data-testid="hero-open-app" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto group bg-transparent border border-white/20 text-zinc-200 hover:bg-white/5 hover:text-white font-medium rounded-full px-7 h-12 text-base"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm">
            <InstallButton
              variant="link"
              label={t("hero.ctaInstallWeb")}
              testId="hero-install-cta"
            />
            <a
              href="#how"
              className="text-zinc-400 hover:text-white transition-colors font-medium"
              data-testid="hero-how-it-works"
            >
              {t("hero.ctaSecondary")}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={4}
          className="mt-8 md:mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-xs text-zinc-500"
        >
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-[#ccff00]" />
            {t("hero.trust.free")}
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-[#ccff00]" />
            {t("hero.trust.malta")}
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-3.5 h-3.5 text-[#ccff00]" />
            {t("hero.trust.nosignup")}
          </div>
        </motion.div>
      </div>

      {/* Phone mockup */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-5 relative flex justify-center items-center order-first lg:order-last"
      >
        <div className="relative">
          <div className="absolute -inset-10 bg-[#ccff00]/[0.06] blur-[60px] rounded-full pointer-events-none" />
          <div className="animate-float relative">
            <img
              src={PHONE_IMG}
              alt="SpotDrop Malta app preview"
              width={800}
              height={800}
              fetchpriority="high"
              decoding="async"
              className="relative w-[240px] sm:w-[300px] lg:w-[400px] h-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              data-testid="hero-phone-mockup"
            />
          </div>

          <MapPinFloating className="top-6 left-2 lg:-left-10" delay={0.6} />
          <MapPinFloating className="bottom-16 right-2 lg:-right-8" delay={0.9} />
          <MapPinFloating className="top-1/2 left-0 lg:-left-14" delay={1.2} />
        </div>
      </motion.div>
    </div>
  </section>
  );
};

const HowItWorks = () => {
  const { t } = useLang();
  const steps = [
    {
      icon: <Compass className="w-5 h-5" />,
      tag: t("how.card1.tag"),
      title: t("how.card1.title"),
      desc: t("how.card1.desc"),
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      tag: t("how.card2.tag"),
      title: t("how.card2.title"),
      desc: t("how.card2.desc"),
    },
    {
      icon: <Clock className="w-5 h-5" />,
      tag: t("how.card3.tag"),
      title: t("how.card3.title"),
      desc: t("how.card3.desc"),
    },
  ];

  return (
    <section id="how" className="relative py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
          className="max-w-2xl"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
            {t("how.eyebrow")}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3 md:mt-4">
            {t("how.title")}
          </h2>
          <p className="text-zinc-400 mt-3 md:mt-4 text-base md:text-lg">
            {t("how.subtitle")}
          </p>
        </motion.div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
              custom={i}
              className="group relative p-6 md:p-8 rounded-2xl bg-[#141416] border border-white/10 hover:border-[#ccff00]/40 transition-all duration-300 overflow-hidden"
              data-testid={`how-card-${i + 1}`}
            >
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#ccff00]/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/20">
                  {s.icon}
                </div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mt-5 md:mt-6">
                  {s.tag}
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-medium mt-2">
                  {s.title}
                </h3>
                <p className="text-zinc-400 mt-2 md:mt-3 leading-relaxed text-sm md:text-[15px]">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Features = () => {
  const { t } = useLang();
  const features = [
    { icon: <Radio className="w-5 h-5" />, title: t("features.card1.title"), desc: t("features.card1.desc") },
    { icon: <Compass className="w-5 h-5" />, title: t("features.card2.title"), desc: t("features.card2.desc") },
    { icon: <Accessibility className="w-5 h-5" />, title: t("features.card3.title"), desc: t("features.card3.desc") },
    { icon: <MapPinned className="w-5 h-5" />, title: t("features.card4.title"), desc: t("features.card4.desc") },
    { icon: <Zap className="w-5 h-5" />, title: t("features.card5.title"), desc: t("features.card5.desc") },
    { icon: <Users className="w-5 h-5" />, title: t("features.card6.title"), desc: t("features.card6.desc") },
  ];

  return (
    <section id="features" className="relative py-20 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6"
        >
          <div className="max-w-2xl">
            <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
              {t("features.eyebrow")}
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3 md:mt-4">
              {t("features.title")}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs md:text-sm">
            <Sparkles className="w-4 h-4 text-[#ccff00]" />
            {t("features.tag")}
          </div>
        </motion.div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
              custom={i}
              className={`group relative p-6 md:p-8 rounded-2xl bg-[#141416] border border-white/10 hover:border-[#ccff00]/40 transition-all duration-300 ${
                i === 0 ? "md:col-span-3" : i === 1 ? "md:col-span-3" : "md:col-span-2"
              }`}
              data-testid={`feature-card-${i + 1}`}
            >
              <div className="flex items-start justify-between">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/20">
                  {f.icon}
                </div>
                <span className="text-[10px] md:text-xs text-zinc-600 font-mono">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-heading text-lg md:text-2xl font-medium mt-5 md:mt-6">
                {f.title}
              </h3>
              <p className="text-zinc-400 mt-2 leading-relaxed text-sm md:text-[15px]">
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Waitlist = () => {
  const { t } = useLang();
  const [form, setForm] = React.useState({ name: "", email: "", area: "" });
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const area = form.area.trim();

    if (!name || !email || !area) {
      const m = "Please fill in all fields";
      setErrorMsg(m);
      toast.error(m);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const m = "Please enter a valid email";
      setErrorMsg(m);
      toast.error(m);
      return;
    }

    setLoading(true);
    setSuccess(false);
    try {
      await axios.post(`${API}/waitlist`, { name, email, area });
      setSuccess(true);
      toast.success(t("updates.successTitle"));
      setForm({ name: "", email: "", area: "" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 409) {
        msg = t("updates.alreadyOn");
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "Something went wrong. Try again.";
      }
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative py-12 md:py-16 border-t border-white/5" data-testid="waitlist-section">
      <div className="max-w-3xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
          className="text-center"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
            {t("updates.eyebrow")}
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-semibold tracking-tight mt-2">
            {t("updates.title")}
          </h2>
          <p className="text-zinc-500 mt-2 text-xs md:text-sm">
            {t("updates.sub")}
          </p>
        </motion.div>

        <motion.form
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          onSubmit={onSubmit}
          className="mt-6 md:mt-8 p-5 md:p-6 rounded-2xl bg-[#141416] border border-white/10"
          data-testid="waitlist-form"
        >
          {success && (
            <div
              className="mb-5 p-4 rounded-xl bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-start gap-3"
              data-testid="waitlist-success"
              role="status"
              aria-live="polite"
            >
              <div className="mt-0.5 w-6 h-6 rounded-full bg-[#ccff00] flex items-center justify-center shrink-0">
                <Check className="w-3.5 h-3.5 text-[#0a0a0c]" strokeWidth={3} />
              </div>
              <div>
                <div className="font-heading font-semibold text-[#ccff00]">
                  {t("updates.successTitle")}
                </div>
                <div className="text-sm text-zinc-300 mt-1">
                  {t("updates.successBody")}
                </div>
              </div>
            </div>
          )}
          {errorMsg && !success && (
            <div
              className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/40 flex items-start gap-3"
              data-testid="waitlist-error"
              role="alert"
              aria-live="assertive"
            >
              <div className="mt-0.5 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center shrink-0">
                <span className="text-white text-sm font-bold leading-none">!</span>
              </div>
              <div>
                <div className="font-heading font-semibold text-red-300">
                  {errorMsg}
                </div>
                {errorMsg === t("updates.alreadyOn") && (
                  <div className="text-sm text-zinc-400 mt-1">
                    {t("updates.alreadyBody")}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-1">
              <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                {t("updates.field.name")}
              </label>
              <Input
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrorMsg(""); }}
                placeholder={t("updates.field.namePlaceholder")}
                className="mt-2 h-12 bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#ccff00] focus-visible:ring-1 focus-visible:border-[#ccff00] rounded-xl"
                data-testid="waitlist-name-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                {t("updates.field.email")}
              </label>
              <Input
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrorMsg(""); }}
                placeholder={t("updates.field.emailPlaceholder")}
                className="mt-2 h-12 bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#ccff00] focus-visible:ring-1 focus-visible:border-[#ccff00] rounded-xl"
                data-testid="waitlist-email-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                {t("updates.field.area")}
              </label>
              <Input
                value={form.area}
                onChange={(e) => { setForm({ ...form, area: e.target.value }); setErrorMsg(""); }}
                placeholder={t("updates.field.areaPlaceholder")}
                className="mt-2 h-12 bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#ccff00] focus-visible:ring-1 focus-visible:border-[#ccff00] rounded-xl"
                data-testid="waitlist-area-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || success}
            className="w-full mt-6 h-12 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-xl text-base disabled:opacity-70"
            data-testid="waitlist-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("updates.submitting")}
              </>
            ) : success ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t("updates.successTitle")}
              </>
            ) : (
              <>
                {t("updates.submit")}
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>

          <p className="text-center text-xs text-zinc-500 mt-4 uppercase tracking-[0.18em]" data-testid="launch-note">
            Live in Malta · Free to use
          </p>
          <div
            className="mt-5 p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-start gap-2.5 text-[11px] md:text-xs text-zinc-500 leading-relaxed"
            data-testid="waitlist-bluebadge-note"
          >
            <Info className="w-3.5 h-3.5 mt-0.5 text-zinc-500 shrink-0" />
            <span>
              SpotDrop only helps users share parking information. Always follow local
              parking laws, road signs, time restrictions, resident permits and Blue
              Badge rules.{" "}
              <strong className="text-zinc-300">
                Disabled parking bays are only for authorised Blue Badge holders.
              </strong>
            </span>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
  <footer className="relative pt-16 md:pt-20 pb-8 border-t border-white/5 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 md:gap-10">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#ccff00] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#0a0a0c]" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold tracking-tight">
              SpotDrop<span className="text-[#ccff00]">.</span> Malta
            </span>
          </div>
          <p className="text-zinc-500 text-sm mt-4 max-w-xs">
            {t("footer.tagline")}
          </p>
        </div>

        <div className="flex flex-col md:items-end gap-4">
          <a
            href="https://instagram.com/spotdropmalta"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#ccff00]/50 hover:bg-[#ccff00]/5 transition-all text-sm text-zinc-300 w-fit"
            data-testid="footer-instagram"
            aria-label={t("footer.instagramAlt")}
          >
            <Instagram className="w-4 h-4 text-[#ccff00]" />
            @spotdropmalta
          </a>
          <nav className="flex flex-wrap md:justify-end gap-x-5 gap-y-1 text-xs text-zinc-500" data-testid="footer-legal-links">
            <a href="#invite" className="hover:text-white transition-colors" data-testid="footer-invite">
              Invite friends
            </a>
            <a href="#waitlist" className="hover:text-white transition-colors" data-testid="footer-updates">
              {t("footer.updates")}
            </a>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">
              {t("footer.terms")}
            </a>
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">
              {t("footer.privacy")}
            </a>
            <a href="/disclaimer" className="hover:text-white transition-colors" data-testid="footer-disclaimer">
              {t("footer.disclaimer")}
            </a>
          </nav>
          <div className="text-xs text-zinc-600">
            {t("footer.copyright").replace("{year}", String(new Date().getFullYear()))}
          </div>
        </div>
      </div>

      <div className="mt-12 md:mt-16 -mb-4 md:-mb-6 select-none pointer-events-none overflow-hidden">
        <div
          className="font-heading font-bold tracking-tighter text-[16vw] md:text-[18vw] leading-none text-white/[0.03] text-center whitespace-nowrap"
          aria-hidden
        >
          SPOTDROP
        </div>
      </div>
    </div>
  </footer>
  );
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white font-body antialiased overflow-x-hidden w-full max-w-[100vw]" data-testid="landing-root">
      <Nav />
      <main>
        <Hero />
        <CampaignTeaser />
        <UseCases />
        <HowItWorks />
        <Features />
        <AppShowcase />
        <InviteSection />
        <Roadmap />
        <SupportSection />
        <Waitlist />
      </main>
      <Footer />
      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
}
